import type { Post } from "shared/types/post";

/** moment format shared by the board card and the preview drawer. */
export const DATE_FORMAT = "ddd D MMM YYYY, h:mm A";

export type Stage =
  "draft" | "scheduled" | "queued" | "published" | "failed" | "cancelled";

// Figma PostStage variants: bg / text colour / leading icon per stage. Shared by
// the board card (`board-post-card`) and the preview drawer — both render the same
// Figma `PostStage` component (scheduled = Blue/100 #dcd6ff fill, brand1 #4f31ff
// text/icon, i.e. the project's blue-100 / blue-500 tokens).
export const STAGE_STYLES: Record<
  Stage,
  { label: string; bg: string; text: string; icon: string }
> = {
  draft: {
    label: "Draft",
    bg: "bg-neutral-50",
    text: "text-neutral-800",
    icon: "fa-file-lines",
  },
  scheduled: {
    label: "Scheduled",
    bg: "bg-blue-100",
    text: "text-blue-500",
    icon: "fa-clock",
  },
  queued: {
    label: "Queued",
    // Figma Queued variant: Brand/Miami/200 fill + Brand/Grape/600 (#9211b2)
    // text/icon. Grape/600 isn't in the Tailwind palette, so it's an arbitrary
    // value (the project's `grape` token is a different shade).
    bg: "bg-miami-200",
    text: "text-[#9211b2]",
    icon: "fa-layer-group",
  },
  published: {
    label: "Published",
    bg: "bg-green-100",
    text: "text-green-600",
    icon: "fa-circle-check",
  },
  failed: {
    label: "Failed",
    bg: "bg-red-100",
    text: "text-red-600",
    icon: "fa-circle-xmark",
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-neutral-25",
    text: "text-neutral-500",
    icon: "fa-circle-xmark",
  },
};

export function resolveStage(post: Post): Stage {
  if (post.postType === "draft") return "draft";
  // `isCanceled` is an untyped runtime field (see PostStatus.js / the post
  // actions dropdown). A cancelled post wins over both a failReason and the
  // success flag, matching those surfaces.
  if ((post as { isCanceled?: boolean }).isCanceled) return "cancelled";
  // A potential upload error is persisted as isSuccess:true AND
  // isPotentialError:true, so it must be checked before isSuccess — otherwise
  // the Archive card reads a green "Published" for a post that may never have
  // gone out, hiding the re-post prompt.
  if (post.isPotentialError || post.failReason) return "failed";
  if (post.isSuccess) return "published";
  if (post.queued) return "queued";
  return "scheduled";
}

/**
 * Figma `PostStage` pill: a leading stage icon + label in the stage's colour,
 * on a rounded tinted chip. Identical token set on the board card and the
 * preview drawer's metadata area.
 */
export const PostStagePill = ({
  post,
  size = "md",
  showIcon = true,
}: {
  post: Post;
  size?: "sm" | "md";
  showIcon?: boolean;
}) => {
  const stage = resolveStage(post);
  const style = STAGE_STYLES[stage];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-[4px] ${style.bg} ${
        size === "sm" ? "px-1 py-px" : "px-2 py-0.5"
      }`}
    >
      {showIcon && (
        <span
          className={`flex shrink-0 items-center justify-center ${
            size === "sm" ? "size-[10px]" : "size-[15px]"
          }`}
        >
          <i
            className={`fa-regular ${style.icon} ${style.text} ${
              size === "sm" ? "text-[8px]" : "text-[12px]"
            }`}
            aria-hidden
          />
        </span>
      )}
      <span
        className={`font-inter font-medium ${style.text} ${
          size === "sm" ? "text-[10px] leading-[14px]" : "text-xs leading-4"
        }`}
      >
        {style.label}
      </span>
    </span>
  );
};
